import { useState, useRef, useEffect } from 'react';
import { useScore } from '../../context/ScoreContext';
import { useComments } from '../../hooks/useComments';

type AddCommentFn = (displayName: string, message: string, team: 'black' | 'white' | null, memberId: string | null) => Promise<void>;

interface CommentModalProps {
  onClose: () => void;
  addComment?: AddCommentFn;
}

const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";
const FONT_HUD  = "'Orbitron', monospace";
const FONT_DECO = "'Cinzel', serif";

const MAX_CHARS = 60;

export default function CommentModal({ onClose, addComment: addCommentProp }: CommentModalProps) {
  const { members, myMemberId } = useScore();
  const { addComment: addCommentHook } = useComments();

  const addComment: AddCommentFn = addCommentProp ?? addCommentHook;
  const myMember = members.find(m => m.id === myMemberId) ?? null;

  const [nickname, setNickname]   = useState(myMember?.name ?? '');
  const [message, setMessage]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]           = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [focused, setFocused]     = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 200);
  }, []);

  const teamColor = myMember?.team === 'black' ? '#d4a843' : myMember?.team === 'white' ? '#e2e2e2' : '#a78bfa';
  const teamRgb   = myMember?.team === 'black' ? '212,168,67' : myMember?.team === 'white' ? '226,226,226' : '167,139,250';
  const teamLabel = myMember?.team === 'black' ? '⬛ 黑隊' : myMember?.team === 'white' ? '⬜ 白隊' : null;

  const effectiveName = isAnonymous ? '匿名' : (myMember?.name ?? nickname);
  const canSubmit = effectiveName.trim().length > 0 && message.trim().length > 0 && !submitting;
  const charPct   = Math.min((message.length / MAX_CHARS) * 100, 100);

  const fuelColor =
    message.length > 50 ? '#e05555' :
    message.length > 38 ? '#e07a30' :
    message.length > 20 ? '#7ec8a0' : teamColor;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    await addComment(
      effectiveName.trim(),
      message.trim(),
      myMember?.team ?? null,
      isAnonymous ? null : myMemberId,
    );
    setDone(true);
    setTimeout(() => {
      onClose();
      // 關閉後自動捲到留言板
      requestAnimationFrame(() => {
        const liveSection = document.getElementById('live');
        if (liveSection) {
          const top = liveSection.getBoundingClientRect().top + window.pageYOffset - 8;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      });
    }, 1800);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">

      {/* ── Backdrop ── */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(2,2,10,0.92)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
      />

      {/* ── Modal ── */}
      <div
        className="relative w-full max-w-md z-10 rounded-2xl overflow-hidden comment-modal-enter"
        style={{
          background: `linear-gradient(160deg, #07112a 0%, #040c1e 50%, #050e20 100%)`,
          border: `1.5px solid rgba(${teamRgb},0.28)`,
          boxShadow: `0 0 0 1px rgba(${teamRgb},0.08), 0 0 60px rgba(${teamRgb},0.18), 0 40px 100px rgba(0,0,0,0.9)`,
        }}
      >

        {/* ── Ambient interior glow ── */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
          <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-80 h-40 blur-[60px] opacity-20 rounded-full"
            style={{ background: `radial-gradient(ellipse, rgba(${teamRgb},1), transparent 70%)` }} />
          <div className="absolute bottom-0 right-0 w-48 h-40 blur-[50px] opacity-10 rounded-full"
            style={{ background: `radial-gradient(ellipse, rgba(${teamRgb},0.8), transparent 70%)` }} />
          {/* Dot-grid overlay */}
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: `radial-gradient(circle, rgba(${teamRgb},1) 1px, transparent 1px)`, backgroundSize: '20px 20px' }} />
          {/* Scanlines */}
          <div className="absolute inset-0 opacity-[0.015]"
            style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.6) 3px, rgba(255,255,255,0.6) 4px)' }} />
        </div>

        {/* ── Top scan beam ── */}
        <div className="absolute top-0 left-0 right-0 h-px overflow-hidden z-20">
          <div className="h-full"
            style={{ background: `linear-gradient(90deg, transparent 0%, rgba(${teamRgb},0.9) 50%, transparent 100%)`, animation: 'commentScan 2s linear infinite' }} />
        </div>

        {/* ── Corner brackets HUD ── */}
        <div className="absolute top-0 left-0 pointer-events-none z-20">
          <div className="w-5 h-5 border-l-2 border-t-2 m-3" style={{ borderColor: `rgba(${teamRgb},0.7)` }} />
        </div>
        <div className="absolute top-0 right-0 pointer-events-none z-20">
          <div className="w-5 h-5 border-r-2 border-t-2 m-3" style={{ borderColor: `rgba(${teamRgb},0.7)` }} />
        </div>
        <div className="absolute bottom-0 left-0 pointer-events-none z-20">
          <div className="w-5 h-5 border-l-2 border-b-2 m-3" style={{ borderColor: `rgba(${teamRgb},0.35)` }} />
        </div>
        <div className="absolute bottom-0 right-0 pointer-events-none z-20">
          <div className="w-5 h-5 border-r-2 border-b-2 m-3" style={{ borderColor: `rgba(${teamRgb},0.35)` }} />
        </div>

        {/* ── Header ── */}
        <div
          className="relative px-5 py-4 flex items-center justify-between z-10"
          style={{
            borderBottom: `1px solid rgba(${teamRgb},0.15)`,
            background: `linear-gradient(90deg, rgba(${teamRgb},0.10) 0%, transparent 65%)`,
          }}
        >
          <div className="flex items-center gap-3.5">
            {/* Pulsing icon */}
            <div className="relative shrink-0">
              <div
                className="absolute inset-0 rounded-xl animate-ping opacity-25"
                style={{ background: `rgba(${teamRgb},0.4)` }}
              />
              <div
                className="relative w-10 h-10 flex items-center justify-center rounded-xl"
                style={{
                  background: `linear-gradient(135deg, rgba(${teamRgb},0.22), rgba(${teamRgb},0.08))`,
                  border: `1.5px solid rgba(${teamRgb},0.5)`,
                  boxShadow: `0 0 16px rgba(${teamRgb},0.4), inset 0 1px 0 rgba(${teamRgb},0.2)`,
                }}
              >
                <i className="ri-megaphone-fill text-lg" style={{ color: teamColor, filter: `drop-shadow(0 0 6px rgba(${teamRgb},0.9))` }} />
              </div>
            </div>
            <div>
              <h3
                className="text-white font-black tracking-wider leading-tight"
                style={{ fontFamily: FONT_BODY, fontSize: '1rem', textShadow: `0 0 14px rgba(${teamRgb},0.6)` }}
              >
                我要說話
              </h3>
              <p
                className="text-[10px] mt-0.5 flex items-center gap-1"
                style={{ color: `rgba(${teamRgb},0.6)`, fontFamily: FONT_BODY }}
              >
                <span className="relative flex h-1.5 w-1.5 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#7ec8a0' }} />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: '#7ec8a0' }} />
                </span>
                留言即時出現在戰場留言板
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span
              className="font-black tracking-[0.3em] uppercase px-2 py-1 rounded text-[9px]"
              style={{
                background: `rgba(${teamRgb},0.10)`,
                color: `rgba(${teamRgb},0.65)`,
                border: `1px solid rgba(${teamRgb},0.22)`,
                fontFamily: FONT_DECO,
              }}
            >
              BROADCAST
            </span>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer transition-all"
              style={{ background: '#071224', border: '1px solid #0e2040', color: '#555' }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.color = teamColor;
                (e.currentTarget as HTMLElement).style.borderColor = `rgba(${teamRgb},0.4)`;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.color = '#555';
                (e.currentTarget as HTMLElement).style.borderColor = '#252525';
              }}
            >
              <i className="ri-close-line text-base" />
            </button>
          </div>
        </div>

        {/* ── Done state ── */}
        {done ? (
          <div className="px-6 py-14 flex flex-col items-center justify-center gap-5">
            {/* Energy ring */}
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full"
                style={{ background: `radial-gradient(circle, rgba(${teamRgb},0.3), transparent 70%)`, animation: 'donePulse 0.8s ease-out' }}
              />
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center relative z-10"
                style={{
                  background: `radial-gradient(circle at 40% 40%, rgba(${teamRgb},0.25), rgba(${teamRgb},0.06))`,
                  border: `2px solid rgba(${teamRgb},0.6)`,
                  boxShadow: `0 0 30px rgba(${teamRgb},0.45), 0 0 60px rgba(${teamRgb},0.2), inset 0 0 20px rgba(${teamRgb},0.1)`,
                  animation: 'doneRing 1.2s ease-out',
                }}
              >
                <i className="ri-send-plane-2-fill text-3xl" style={{ color: teamColor, filter: `drop-shadow(0 0 10px rgba(${teamRgb},1))` }} />
              </div>
              {/* Orbiting particles */}
              {[0, 120, 240].map(deg => (
                <div
                  key={deg}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    top: '50%', left: '50%',
                    background: teamColor,
                    boxShadow: `0 0 8px ${teamColor}`,
                    transform: `rotate(${deg}deg) translateX(44px) translateY(-50%)`,
                    animation: `orbitShard 1.5s linear infinite`,
                    animationDelay: `${deg / 360}s`,
                    opacity: 0.8,
                  }}
                />
              ))}
            </div>
            <div className="text-center">
              <p
                className="font-black tracking-[0.3em] uppercase text-base mb-1"
                style={{ color: teamColor, fontFamily: FONT_DECO, textShadow: `0 0 20px rgba(${teamRgb},0.8)` }}
              >
                訊號發射！
              </p>
              <p style={{ color: '#666', fontSize: '11px', fontFamily: FONT_BODY }}>你的聲音已傳遍整個戰場</p>
            </div>
            <div className="flex items-center gap-2">
              {[0, 120, 240, 360, 480].map(d => (
                <div
                  key={d}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: teamColor, animation: `waveDot 0.9s ${d}ms ease-in-out infinite`, opacity: 0.7, boxShadow: `0 0 4px ${teamColor}` }}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="px-5 py-5 space-y-4 relative z-10">

            {/* ── Identity section ── */}
            <div>
              <div className="flex items-center justify-between mb-2.5">
                <span
                  className="text-[10px] font-black tracking-[0.4em] uppercase flex items-center gap-1.5"
                  style={{ color: `rgba(${teamRgb},0.65)`, fontFamily: FONT_DECO }}
                >
                  <i className="ri-user-star-line" />IDENTITY
                </span>
                <button
                  onClick={() => setIsAnonymous(v => !v)}
                  className="flex items-center gap-1.5 text-[10px] px-3 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap font-black tracking-wide"
                  style={isAnonymous ? {
                    background: '#0a1828',
                    border: '1px solid #1a3048',
                    color: '#bbb',
                    fontFamily: FONT_BODY,
                  } : {
                    background: 'transparent',
                    border: '1px solid #0d1e2e',
                    color: '#555',
                    fontFamily: FONT_BODY,
                  }}
                >
                  <i className={`${isAnonymous ? 'ri-ghost-fill' : 'ri-ghost-line'} text-xs`} />
                  {isAnonymous ? '匿名中' : '匿名'}
                </button>
              </div>

              {isAnonymous ? (
                <div
                  className="flex items-center gap-3 px-4 py-3 rounded-xl relative overflow-hidden"
                  style={{ background: '#06101e', border: '1px solid #0e1e30' }}
                >
                  {/* Ghost shimmer */}
                  <div className="absolute inset-0 opacity-10 pointer-events-none"
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)', animation: 'ghostSlide 3s linear infinite' }} />
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 relative"
                    style={{ background: '#0a1828', border: '1px solid #122036' }}>
                    <i className="ri-ghost-2-fill text-xl text-[#555]" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[#888] font-black text-sm" style={{ fontFamily: FONT_HUD }}>???</div>
                    <div className="text-[#3a3a3a] text-[10px] mt-0.5" style={{ fontFamily: FONT_BODY }}>身份已被抹去</div>
                  </div>
                  <i className="ri-eye-off-line text-sm text-[#2a4058]" />
                </div>
              ) : myMember ? (
                <div
                  className="flex items-center gap-3 px-4 py-3 rounded-xl relative overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, rgba(${teamRgb},0.10), rgba(${teamRgb},0.03))`,
                    border: `1px solid rgba(${teamRgb},0.30)`,
                    boxShadow: `inset 0 0 20px rgba(${teamRgb},0.05)`,
                  }}
                >
                  {/* Left glow line */}
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l-xl"
                    style={{ background: `linear-gradient(180deg, rgba(${teamRgb},0.8), rgba(${teamRgb},0.2))` }} />
                  {myMember.avatarUrl ? (
                    <img
                      src={myMember.avatarUrl}
                      alt={myMember.name}
                      className="w-10 h-10 rounded-full object-cover object-top shrink-0"
                      style={{ border: `2px solid rgba(${teamRgb},0.55)`, boxShadow: `0 0 12px rgba(${teamRgb},0.35)` }}
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black shrink-0"
                      style={{
                        background: `radial-gradient(circle at 35% 35%, rgba(${teamRgb},0.28), rgba(${teamRgb},0.06))`,
                        color: teamColor,
                        border: `2px solid rgba(${teamRgb},0.55)`,
                        boxShadow: `0 0 12px rgba(${teamRgb},0.3)`,
                        fontFamily: FONT_BODY,
                      }}
                    >
                      {myMember.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <span
                      className="text-white font-black text-sm"
                      style={{ fontFamily: FONT_BODY, textShadow: `0 0 8px rgba(${teamRgb},0.4)` }}
                    >
                      {myMember.name}
                    </span>
                    {teamLabel && (
                      <span
                        className="ml-2 text-[9px] px-2 py-0.5 rounded-full font-black tracking-widest"
                        style={{ background: `rgba(${teamRgb},0.18)`, color: teamColor, border: `1px solid rgba(${teamRgb},0.35)`, fontFamily: FONT_BODY }}
                      >
                        {teamLabel}
                      </span>
                    )}
                    <div className="text-[10px] mt-0.5 flex items-center gap-1" style={{ color: '#2a2a2a', fontFamily: FONT_BODY }}>
                      <i className="ri-shield-check-line" style={{ color: `rgba(${teamRgb},0.5)` }} />身份已驗證
                    </div>
                  </div>
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0"
                    style={{ background: `rgba(${teamRgb},0.12)`, border: `1px solid rgba(${teamRgb},0.25)` }}
                  >
                    <i className="ri-verified-badge-fill text-base" style={{ color: teamColor }} />
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={nickname}
                    onChange={e => setNickname(e.target.value)}
                    placeholder="輸入你的代號…"
                    maxLength={20}
                    className="w-full rounded-xl px-4 py-3 text-white text-sm outline-none placeholder:text-[#1e3050] transition-all"
                    style={{
                      background: '#060f1e',
                      border: `1px solid rgba(${teamRgb},0.25)`,
                      fontFamily: FONT_BODY,
                    }}
                  />
                  <i className="ri-user-3-line absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: `rgba(${teamRgb},0.35)` }} />
                </div>
              )}
            </div>

            {/* ── Message area ── */}
            <div>
              <div className="flex items-center justify-between mb-2.5">
                <span
                  className="text-[10px] font-black tracking-[0.4em] uppercase flex items-center gap-1.5"
                  style={{ color: `rgba(${teamRgb},0.65)`, fontFamily: FONT_DECO }}
                >
                  <i className="ri-radio-line" />BROADCAST
                </span>
                <div className="flex items-center gap-2">
                  <span
                    className="text-[10px] font-black tabular-nums"
                    style={{ color: fuelColor, fontFamily: FONT_HUD }}
                  >
                    {message.length}/{MAX_CHARS}
                  </span>
                </div>
              </div>

              {/* Fuel gauge */}
              <div className="relative h-1 rounded-full mb-2.5 overflow-hidden" style={{ background: '#06101e', border: '1px solid #0d1e30' }}>
                <div
                  className="h-full rounded-full transition-all duration-200"
                  style={{
                    width: `${charPct}%`,
                    background: `linear-gradient(90deg, rgba(${teamRgb},0.5), ${fuelColor})`,
                    boxShadow: `0 0 8px ${fuelColor}88`,
                  }}
                />
                {/* Fuel indicator tip */}
                {message.length > 0 && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full"
                    style={{
                      left: `calc(${charPct}% - 3px)`,
                      background: fuelColor,
                      boxShadow: `0 0 6px ${fuelColor}`,
                      transition: 'left 0.2s',
                    }}
                  />
                )}
              </div>

              {/* Textarea */}
              <div
                className="relative rounded-xl overflow-hidden transition-all duration-200"
                style={{
                  border: focused
                    ? `1px solid rgba(${teamRgb},0.55)`
                    : '1px solid #0e1e30',
                  boxShadow: focused
                    ? `0 0 20px rgba(${teamRgb},0.18), inset 0 0 12px rgba(${teamRgb},0.04)`
                    : 'none',
                }}
              >
                {/* HUD corner marks */}
                {focused && (
                  <>
                    <div className="absolute top-1.5 left-1.5 w-2.5 h-2.5 pointer-events-none z-10" style={{ borderLeft: `1px solid rgba(${teamRgb},0.5)`, borderTop: `1px solid rgba(${teamRgb},0.5)` }} />
                    <div className="absolute top-1.5 right-1.5 w-2.5 h-2.5 pointer-events-none z-10" style={{ borderRight: `1px solid rgba(${teamRgb},0.5)`, borderTop: `1px solid rgba(${teamRgb},0.5)` }} />
                    <div className="absolute bottom-1.5 left-1.5 w-2.5 h-2.5 pointer-events-none z-10" style={{ borderLeft: `1px solid rgba(${teamRgb},0.3)`, borderBottom: `1px solid rgba(${teamRgb},0.3)` }} />
                    <div className="absolute bottom-1.5 right-1.5 w-2.5 h-2.5 pointer-events-none z-10" style={{ borderRight: `1px solid rgba(${teamRgb},0.3)`, borderBottom: `1px solid rgba(${teamRgb},0.3)` }} />
                  </>
                )}
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={e => setMessage(e.target.value.slice(0, MAX_CHARS))}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  placeholder="為隊友加油、挑釁對手、說出你的心聲…"
                  rows={3}
                  className="w-full px-4 py-3 text-white text-sm outline-none resize-none placeholder:text-[#1a2e48] transition-colors duration-200"
                  style={{
                    background: focused ? `rgba(${teamRgb},0.04)` : '#040c1a',
                    fontFamily: FONT_BODY,
                    lineHeight: 1.7,
                  }}
                />
                {/* Bottom glow line when focused */}
                {focused && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-px"
                    style={{ background: `linear-gradient(90deg, transparent, rgba(${teamRgb},0.7), transparent)` }}
                  />
                )}
              </div>

              <p
                className="text-[10px] mt-1.5 flex items-center gap-1.5"
                style={{ color: '#2a2a2a', fontFamily: FONT_BODY }}
              >
                <i className="ri-keyboard-line" />
                <span>Ctrl + Enter 快速發射</span>
              </p>
            </div>

            {/* ── Submit button ── */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="relative w-full py-4 rounded-xl font-black text-sm tracking-[0.25em] uppercase overflow-hidden cursor-pointer whitespace-nowrap transition-all duration-250 active:scale-[0.97]"
              style={canSubmit ? {
                background: `linear-gradient(135deg, rgba(${teamRgb},0.85) 0%, ${teamColor} 50%, rgba(${teamRgb},0.75) 100%)`,
                color: '#0a0a0a',
                boxShadow: `0 0 28px rgba(${teamRgb},0.55), 0 0 56px rgba(${teamRgb},0.20), 0 4px 14px rgba(${teamRgb},0.35)`,
                border: `1px solid rgba(${teamRgb},0.8)`,
                fontFamily: FONT_DECO,
              } : {
                background: '#071020',
                color: '#2a4060',
                border: '1px solid #0d1e30',
                fontFamily: FONT_DECO,
              }}
            >
              {/* Shimmer */}
              {canSubmit && (
                <div
                  className="absolute inset-0 opacity-40 pointer-events-none"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)', animation: 'btnShimmer 2s linear infinite' }}
                />
              )}
              {/* Particle sparks when ready */}
              {canSubmit && !submitting && (
                <>
                  {[20, 50, 80].map(pos => (
                    <div
                      key={pos}
                      className="absolute bottom-0 w-px"
                      style={{
                        left: `${pos}%`,
                        height: '6px',
                        background: `rgba(${teamRgb},0.6)`,
                        animation: `btnSpark 1.2s ${pos * 8}ms ease-out infinite`,
                      }}
                    />
                  ))}
                </>
              )}
              <span className="relative z-10 flex items-center justify-center gap-2.5">
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line text-base animate-spin" />
                    <span>傳送中…</span>
                  </>
                ) : isAnonymous ? (
                  <>
                    <i className="ri-ghost-fill text-base" />
                    <span>匿名發射</span>
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-2-fill text-base" />
                    <span>發送訊號</span>
                  </>
                )}
              </span>
            </button>

          </div>
        )}
      </div>

      <style>{`
        .comment-modal-enter {
          animation: commentModalIn 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        @keyframes commentModalIn {
          from { opacity: 0; transform: scale(0.92) translateY(12px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
        @keyframes commentScan {
          from { transform: translateX(-100%); }
          to   { transform: translateX(100%); }
        }
        @keyframes ghostSlide {
          from { transform: translateX(-200%); }
          to   { transform: translateX(200%); }
        }
        @keyframes btnShimmer {
          from { transform: translateX(-200%); }
          to   { transform: translateX(200%); }
        }
        @keyframes btnSpark {
          0%   { transform: translateY(0); opacity: 0; }
          20%  { opacity: 1; }
          100% { transform: translateY(-14px); opacity: 0; }
        }
        @keyframes donePulse {
          0%   { transform: scale(0.5); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes doneRing {
          0%   { transform: scale(0.6); opacity: 0; }
          60%  { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes orbitShard {
          from { transform: rotate(var(--start, 0deg)) translateX(44px) translateY(-4px); }
          to   { transform: rotate(calc(var(--start, 0deg) + 360deg)) translateX(44px) translateY(-4px); }
        }
        @keyframes waveDot {
          0%, 100% { opacity: 0.3; transform: scale(0.7); }
          50%       { opacity: 1;   transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
}
